<template>
  <div id="createTestSale">
    <div class="container">

      <div id="last-sale-preview" v-if="!!lastSale">
        <div class="section-title">{{ $t('test_sale.last_sale') }}</div>
        <json-viewer :value="lastSale" :expand-depth="0" />
      </div>

      <div class="section-title">{{ $t('test_sale.new_sale') }}</div>
      <div style="display: flex">
        <div id="main-container" style="flex-grow: 1">
          <div id="detail-table">
            <div class="detail-row">
              <div><button class="btn btn-primary" @click="addDetail">{{ $t('add') }}</button></div>
              <div>{{ $t('item') }}</div>
              <div>{{ $t('quantity') }}</div>
              <div>{{ $t('unitPrice') }}</div>
              <div>{{ $t('taxes.title') }}</div>
            </div>
            <div class="detail-row" v-for="(detail, index) of sale.details" :key="`det_${index}`">
              <div><button class="btn btn-secondary" @click="removeDetail(index)">X</button></div>
              <AjaxSelect v-model="detail.item" api="/api/entity/orderable/select"
                :query="{ inventory: inventoryId, add: ['taxable', 'priceSchemes'] }" :map-fn="(i) => `${$to(i.name)}`"
                :allow-empty-search="true" @changed="onChangedDetailItem(index)" />
              <input type="number" v-model="detail.quantity" :disabled="!detail.item.id">
              <input type="number" v-model="detail.unitPrice" :disabled="!detail.item.id">
              <div>
                <div v-for="tax of companyTaxes" :key="`det_${index}_tax_${tax.uniqueName}`">
                  <input :id="`tax-chk-${index}-${tax.uniqueName}`" :value="tax.uniqueName" v-model="detail.appliedTaxes"
                    type="checkbox">
                  <label :for="`tax-chk-${index}-${tax.uniqueName}`">{{ tax.uniqueName }}</label>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <!-- Contact data -->
              <div class="section-title">{{ $t('test_sale.contact') }}</div>
              <div class="group">
                <label>{{ $t('search') }}</label>
                <AjaxSelect v-model="search.contact" api="/api/sale_data_search/contact" :query="{ company: companyId }"
                  :map-fn="(i) => contactToString(i.data)" :allow-empty-search="true" />
              </div>
              <div class="group">
                <label>{{ $t('email') }}</label>
                <input type="text" v-model="sale.contact.email">
              </div>
              <div class="group">
                <label>{{ $t('phoneNumber') }}</label>
                <input type="text" v-model="sale.contact.phone">
              </div>
              <div class="group">
                <label>{{ $t('test_sale.first_name') }}</label>
                <input type="text" v-model="sale.contact.firstName">
              </div>
              <div class="group">
                <label>{{ $t('test_sale.last_name') }}</label>
                <input type="text" v-model="sale.contact.lastName">
              </div>
              <div class="group">
                <label>{{ $t('test_sale.contact_method') }}</label>
                <select v-model="sale.contact.method">
                  <option v-for="cm of contactMethods" :value="cm" :key="`cm_${cm}`">{{ cm }}</option>
                </select>
              </div>

              <!-- Delivery data -->
              <template v-if="sale.deliveryMethod === 'delivery'">
                <div class="section-title">{{ $t('deliveryMethod.delivery') }}</div>
                <div class="group">
                  <label>{{ $t('search') }}</label>
                  <AjaxSelect v-model="search.delivery" api="/api/sale_data_search/delivery" :query="{ company: companyId }"
                    :map-fn="(i) => addressToString(i.data)" :allow-empty-search="true" />
                </div>
                <div class="group">
                  <label>{{ $t('address.fullAddress') }}</label>
                  <input type="text" v-model="sale.deliveryData.fullAddress">
                </div>
                <div class="group">
                  <label>{{ $t('address.app') }}</label>
                  <input type="text" v-model="sale.deliveryData.app">
                </div>
                <div class="group">
                  <label>{{ $t('address.city') }}</label>
                  <input type="text" v-model="sale.deliveryData.city">
                </div>
                <div class="group">
                  <label>{{ $t('address.zip') }}</label>
                  <input type="text" v-model="sale.deliveryData.zip">
                </div>
                <div class="group">
                  <label>{{ $t('address.state') }}</label>
                  <input type="text" v-model="sale.deliveryData.state">
                </div>
                <div class="group">
                  <label>{{ $t('address.country') }}</label>
                  <input type="text" v-model="sale.deliveryData.country">
                </div>
                <div class="group">
                  <label>{{ $t('address.note') }}</label>
                  <input type="text" v-model="sale.deliveryData.note">
                </div>
              </template>

              <!-- InPlace data -->
              <template v-if="sale.deliveryMethod === 'inPlace'">
                <div class="section-title">{{ $t('deliveryMethod.inPlace') }}</div>
                <div class="group">
                  <label>{{ $t('test_sale.table') }}</label>
                  <select v-model="sale.deliveryData.tableNumber">
                    <option v-for="table of tables" :value="table" :key="`table_${table}`">{{ table }}</option>
                  </select>
                </div>
              </template>

              <!-- Hotel data -->
              <template v-if="sale.deliveryMethod === 'hotel'">
                <div class="section-title">{{ $t('deliveryMethod.hotel') }}</div>
                <div class="group">
                  <label>{{ $t('test_sale.room') }}</label>
                  <select v-model="sale.deliveryData.roomNumber">
                    <option v-for="room of rooms" :value="room" :key="`table_${room}`">{{ room }}</option>
                  </select>
                </div>
              </template>

              <!-- Catering data -->
              <template v-if="sale.deliveryMethod === 'catering'">
                <div class="section-title">{{ $t('deliveryMethod.catering') }}</div>
                <div class="group">
                  <label>{{ $t('test_sale.questions') }}</label>
                  <!-- Add questions -->
                </div>
              </template>

            </div>

            <div class="column">
              <!-- Misc options -->
              <div class="section-title">{{ $t('test_sale.sale_options') }}</div>
              <div class="group">
                <label>{{ $t('test_sale.delivery_method') }}</label>
                <select v-model="sale.deliveryMethod">
                  <option v-for="dm of deliveryMethods" :value="dm" :key="`dm_${dm}`">{{ dm }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('language') }}</label>
                <select v-model="sale.language">
                  <option v-for="language of companyLanguages" :value="language" :key="`loc_${language}`">
                    {{ $t(`translation.${language}`) }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('test_sale.send_to_pos') }}</label><!-- warning: inverted bool -->
                <select v-model="sale.flags.skipPOS">
                  <option :value="false">{{ $t('yes') }}</option>
                  <option :value="true">{{ $t('no') }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('test_sale.as_test_sale') }}</label>
                <select v-model="sale.flags.asTestSale">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>

            </div>
          </div>

        </div>

        <div id="right-side-bar">
          <!-- Sale summary -->
          <div id="sale-summary">
            <div class="section-title">{{ $t('summary') }}</div>
            <div>
              <div>{{ $t('items.title') }}</div>
              <div class="monospace">{{ $tc(detailsTotal, sale.currency) }}</div>
            </div>
            <div v-if="deliveryFeesActivated">
              <div>{{ $t('test_sale.delivery_fees') }}</div>
              <div><input v-model="sale.deliveryFees" type="number" class="summary-input monospace"></div>
            </div>
            <div v-if="serviceFeesActivated">
              <div>{{ $t('test_sale.service_fees') }}</div>
              <div><input v-model="sale.serviceFees" type="number" class="summary-input monospace"></div>
            </div>
            <hr>

            <div>
              <div>{{ $t('subTotal') }}</div>
              <div class="monospace">{{ $tc(subTotal, sale.currency) }}</div>
            </div>
            <div v-for="tax of taxes" :key="`sum_tax_${tax.name}`">
              <div>{{ tax.name }}</div>
              <div class="monospace">{{ $tc(tax.total, sale.currency) }}</div>
            </div>
            <div v-if="tipsActivated">
              <div>{{ $t('test_sale.tips') }}</div>
              <div><input v-model="sale.tipTotal" type="number" class="summary-input monospace"></div>
            </div>
            <hr>

            <div>
              <div>{{ $t('total') }}</div>
              <div class="monospace">{{ $tc(total, sale.currency) }}</div>
            </div>
          </div>

          <!-- Errors -->
          <div id="sale-errors" v-if="hasErrors">
            <div class="section-title">{{ $t('test_sale.errors') }}</div>
            <div class="sale-line" v-for="(error, index) of saleErrors" :key="`err_${index}`">
              <div>{{ $t_errorName(error.e) }}</div>
              <div class="params">
                <div v-for="(value, param) of error.p">{{ $t_errorParam(param) }}: {{ value }}</div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div id="actions">
            <div class="section-title">{{ $t('test_sale.actions') }}</div>
            <button class="btn btn-primary" @click="create" :disabled="hasErrors">{{ $t('create') }}</button>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
  import { Core } from "@/core/Core";
  import AjaxSelect from "@/components/tools/AjaxSelect";
  import JsonViewer from 'vue-json-viewer';

  export default {
    name: "CreateTestSale",
    components: { AjaxSelect, JsonViewer },

    data() {
      return {
        initialDataJsonString: undefined,
        lastSale: null,
        search: {
          delivery: { id: null },
          contact: { id: null }
        },
        sale: {
          appName: 'backend',
          appVersion: '0.9',
          currency: 'CAD',
          deliveryMethod: 'takeout',
          language: 'fr',
          deliveryFees: 0,
          serviceFees: 0,
          tipTotal: 0,
          userComment: null,
          details: [],
          flags: {
            skipPOS: true,
            asTestSale: true
          },
          contact: {
            method: 'email',
            email: undefined,
            phone: undefined,
            firstName: undefined,
            lastName: undefined
          },
          deliveryData: {
            fullAddress: undefined,
            city: undefined,
            state: undefined,
            country: undefined,
            app: undefined,
            lat: undefined,
            lng: undefined,
            zip: undefined,
            note: undefined,

            tableNumber: undefined,
            roomNumber: undefined
          }
        }
      }
    },

    computed: {
      company() {
        return this.$store.state.contextCompany;
      },
      companyId() {
        return this.company ? this.company.id : null;
      },
      inventoryId() {
        return this.company ? this.company.inventory.id : null;
      },
      tipsActivated() {
        return this.company && this.sale.deliveryMethod
          ? this.company.onlineOrdering[this.sale.deliveryMethod].general.tipsActivated
          : false;
      },
      deliveryFeesActivated() {
        return this.sale.deliveryMethod === 'delivery'
      },
      serviceFeesActivated() {
        return !!this.defaultServiceFees;
      },
      defaultDeliveryFees() {
        return this.company && this.sale.deliveryMethod === 'delivery'
          ? (this.company.onlineOrdering.delivery.zones[0] ? this.company.onlineOrdering.delivery.zones[0].fees.amount : 0)
          : null;
      },
      defaultServiceFees() {
        return this.company && this.sale.deliveryMethod
          ? this.company.onlineOrdering[this.sale.deliveryMethod].general.serviceFees.amount
          : null;
      },
      deliveryMethods() {
        return ['takeout', 'delivery', 'inPlace', 'hotel', 'catering']
          .filter(method => this.activated[method]);
      },
      contactMethods() {
        return ['none', 'email', 'sms']
          .filter(method => this.activated[method] || method === 'none');
      },
      tables() {
        return this.company ? (this.company.onlineOrdering.inPlace.tables || []) : [];
      },
      rooms() {
        return this.company ? (this.company.onlineOrdering.hotel.rooms || []) : [];
      },
      activated() {
        return {
          delivery: this.company ? this.company.onlineOrdering.delivery.activated : false,
          takeout: this.company ? this.company.onlineOrdering.takeout.activated : false,
          inPlace: this.company ? this.company.onlineOrdering.inPlace.activated : false,
          hotel: this.company ? this.company.onlineOrdering.hotel.activated : false,
          catering: this.company ? this.company.onlineOrdering.catering.activated : false,

          email: !!this.company, /* always activated */
          sms: this.company ? this.company.smsActivated : false,

        }
      },
      companyLanguages() {
        return this.company ? this.company.languages : ['fr', 'en']
      },
      companyTaxes() {
        return this.company ? this.company.taxes : []
      },

      detailsTotal() {
        return this.sale.details.reduce((acc, det) => acc + (det.item.id ? (det.unitPrice * det.quantity) : 0), 0);
      },
      subTotal() {
        return this.roundWithCurrency(
          parseFloat(this.detailsTotal || 0)
          + (parseFloat(this.sale.serviceFees) || 0)
          + (parseFloat(this.sale.deliveryFees) || 0)
        );
      },
      taxes() {
        return this.companyTaxes.map(ct => {
          let taxableAmount = this.sale.details.reduce(
            (acc, det) => acc + (det.appliedTaxes.includes(ct.uniqueName) ? det.unitPrice * det.quantity : 0),
            0
          );
          return {
            name: ct.uniqueName,
            type: ct.type === 'flat' ? '$' : '%',
            rate: ct.value,
            total: this.roundWithCurrency(taxableAmount * ct.value / 100)
          }
        })
      },
      total() {
        return this.roundWithCurrency(
          (this.subTotal + this.taxes.reduce((acc, tax) => acc + tax.total, 0))
          + (parseFloat(this.sale.tipTotal) || 0)
        )
      },
      hasErrors() {
        return this.saleErrors.length > 0;
      },
      saleErrors() {
        let errors = [];
        if (this.sale.details.length === 0) {
          errors.push({ e: 'empty_sale' });
        }
        if (!this.sale.contact.firstName) {
          errors.push({ e: 'missing_first_name' });
        }
        if (!this.sale.contact.lastName) {
          errors.push({ e: 'missing_last_name' });
        }
        for (let [line, det] of Object.entries(this.sale.details)) {
          if (!det.item.id) {
            errors.push({
              e: 'line_no_item',
              p: { line: line }
            })
          }
          if (det.quantity <= 0) {
            errors.push({
              e: 'line_invalid_quantity',
              p: { line: line, quantity: det.quantity }
            })
          }
        }
        return errors;
      }
    },

    mounted() {
      this.initialDataJsonString = JSON.stringify(this._data);
    },

    watch: {
      'company': {
        immediate: true,
        handler(company) {
          if (company) {
            this.sale.currency = company.currency;
          }
        }
      },
      'deliveryMethods'(methods) {
        this.sale.deliveryMethod = methods.includes('takeout') ? 'takeout' : (methods[0] || 'takeout');
      },
      deliveryFeesActivated(activated) {
        this.sale.deliveryFees = activated ? this.defaultDeliveryFees : 0;
      },
      serviceFeesActivated(activated) {
        this.sale.serviceFees = activated ? this.defaultServiceFees : 0;
      },
      tipsActivated() {
        this.sale.tipTotal = 0;
      },
      'search.contact': {
        deep: true,
        handler(val) {
          if (val && val.data) {
            for (let [k, v] of Object.entries(val.data)) {
              this.sale.contact[k] = v
            }
          }
        }
      },
      'search.delivery': {
        deep: true,
        handler(val) {
          if (val && val.data) {
            for (let [k, v] of Object.entries(val.data)) {
              this.sale.deliveryData[k] = v
            }
          }
        }
      },
      'sale.deliveryMethod'(method) {
        for (let detail of this.sale.details) {
          let item = detail.item;
          if (item.id) {
            detail.unitPrice = this.findPriceByMethod(item, method)
          }
        }
      }
    },

    methods: {
      reset() {
        let parsedInitialData = JSON.parse(this.initialDataJsonString);
        for (let [k, v] of Object.entries(parsedInitialData)) {
          if (k === 'initialData' || k === 'lastSale') { continue; }
          this[k] = v;
        }
      },
      addDetail() {
        this.sale.details.push({
          item: { id: null },
          quantity: 1,
          unitPrice: 0,
          appliedTaxes: []
        })
      },
      removeDetail(index) {
        this.sale.details.splice(index, 1)
      },
      onChangedDetailItem(index) {
        let detail = this.sale.details[index];
        let item = detail.item;
        if (item.id) {
          detail.unitPrice = this.findPriceByMethod(item, this.sale.deliveryMethod);
          detail.appliedTaxes = item.taxable ? this.companyTaxes.map(t => t.uniqueName) : []
        }
      },
      contactToString(contact) {
        if (!this.isObject(contact)) { return ''; }
        return [contact.firstName, contact.lastName, contact.email, contact.phone]
          .filter(e => !!e)
          .join(' ');
      },
      addressToString(address) {
        if (!this.isObject(address)) { return ''; }
        return [address.fullAddress, address.app]
          .filter(e => !!e)
          .join(' ');
      },
      findPriceByMethod(item, method) {
        let scheme = item.priceSchemes.find(ps => ps.method === method)
          || item.priceSchemes.find(ps => ps.method === 'takeout')
          || { unitPrice: 0 };

        return parseFloat(scheme.unitPrice) || 0;
      },
      isObject(obj) {
        return typeof obj === 'object' && obj !== null
      },
      roundWithCurrency(num) {
        return Math.round(num * 100) / 100;
      },
      $t_errorName(errorName) {
        let transKey = `test_sale.error.${errorName}`;
        let translated = this.$t(transKey);
        if (translated === transKey) {
          translated = errorName
        }
        return translated;
      },
      $t_errorParam(paramName) {
        let transKey = `test_sale.error.param.${paramName}`;
        let translated = this.$t(transKey);
        if (translated === transKey) {
          translated = paramName
        }
        return translated;
      },
      async create() {
        if (this.saleErrors.length) {
          toast('Fix errors', 'error')
          return;
        }

        showSpinner();

        let data = JSON.parse(JSON.stringify(this.sale));

        /* remove flags when 'false' */
        data.flags = Object.fromEntries(
          Object.entries(data.flags).filter(([_, v]) => !!v)
        );
        data.company = this.companyId;
        data.tipTotal = parseFloat(data.tipTotal) || 0;
        data.serviceFees = parseFloat(data.serviceFees) || 0;
        data.deliveryFees = parseFloat(data.deliveryFees) || 0;
        data.subTotal = this.subTotal;
        data.taxes = this.taxes;
        data.total = this.total;
        for (let det of data.details) {
          det.item = det.item.id;
          det.unitPrice = parseFloat(det.unitPrice) || 0;
          det.quantity = parseFloat(det.quantity) || 0;
        }
        try {
          this.lastSale = await Core.request('POST', '/api/sale_test', data);
          toast(this.$t("test_sale.send_success"), "success");
          hideSpinner();
          this.reset();
        } catch (e) {
          toast(this.$t("test_sale.send_failed"), "error");
          console.log(e);
          hideSpinner()
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  #last-sale-preview {
    margin-bottom: 15px;
  }

  #detail-table {
    margin-bottom: 40px;
    width: 100%;
  }

  #right-side-bar {
    width: 300px;
    margin-left: 20px;
  }

  #sale-summary {
    div {
      display: flex;
      justify-content: space-between;
    }
  }

  #sale-errors {
    margin-top: 15px;
    color: indianred;

    .sale-line {
      margin-bottom: 5px;
    }

    .params {
      display: flex;

      div {
        margin-right: 10px;
        color: grey;
        font-size: 0.8em;
      }
    }
  }

  #actions {
    margin-top: 15px;
  }

  .detail-row {
    display: flex;
    width: 600px;

    button {
      width: 100%;
    }

    div {
      margin: auto;
      text-align: center;
    }

    &> :nth-child(1) {
      width: 100px;
      padding-right: 10px;
    }

    &> :nth-child(2) {
      width: 200px;
    }

    &> :nth-child(3) {
      width: 100px;
    }

    &> :nth-child(4) {
      width: 100px;
    }

    &> :nth-child(5) {
      width: 100px;
    }
  }

  .section-title {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 14px;
    color: var(--primary-color);
    margin-bottom: 10px;
  }

  .monospace {
    font-family: "Lucida Console", Monaco, monospace
  }

  .summary-input {
    width: 80px;
    text-align: right;
    padding: 0 !important;
    font-size: 16px;
  }
</style>